import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import SideBar from "./SideBar";
import axios from "axios";
import localforage from "localforage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronDown,
  faChevronUp,
  faCheckCircle,
  faSpinner,
  faCheck,
  faClock,
  faCircle,
  faSearch,
  faXmark,
  faListUl,
  faChevronLeft,
  faChevronRight,
  faUserPlus,
  faTrash,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/workspacePage.css";

const API_URL = process.env.REACT_APP_API_URL;

const TASK_SUGGESTIONS = [
  "Social Infra Research",
  "Comm-Narrative & Content Development",
  "Comm-Issues & Crisis Management",
  "Stakeholder Mapping",
  "Partnership Outreach",
  "Comm-Information Dissemination",
  "Comm-Database Management",
  "Comm-Community Insights and Feedback",
  "Comm-Curating a pool of Influencers",
  "Industrial Units Mapping",
  "Additional Support",
];

const STATUS_CONFIG = {
  not_started: {
    label: "Not Started",
    color: "#666666",
    bg: "#F0F0F0",
    icon: faCircle,
  },
  in_progress: {
    label: "In Progress",
    color: "#E65100",
    bg: "#FFF3E0",
    icon: faClock,
  },
  completed: {
    label: "Completed",
    color: "#2E7D32",
    bg: "#E8F5E9",
    icon: faCheckCircle,
  },
};
const STATUS_CYCLE = ["not_started", "in_progress", "completed"];

const getToken = () => localforage.getItem("token");

// ── Week helpers (Wed → Tue cycle) ───────────────────────────────────────────
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day >= 3 ? day - 3 : day + 4;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekEnd = (weekStart) => {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const fmtWeekLabel = (start) => {
  const end = getWeekEnd(start);
  return `${fmtDate(start)} (Wed) — ${fmtDate(end)} (Tue)`;
};

const getCurrentWeekStart = () => getWeekStart(new Date());

// ── Initials & avatar color ───────────────────────────────────────────────────
const getInitials = (email) => {
  if (!email) return "?";
  const parts = email.split("@")[0].split(/[._-]/);
  return (
    parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "?"
  );
};

const AVATAR_COLORS = [
  "#CE452B",
  "#1565C0",
  "#2E7D32",
  "#7B1FA2",
  "#E65100",
  "#00695C",
  "#C62828",
  "#4527A0",
];
const getAvatarColor = (str) => {
  if (!str) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

// ── Avatar pill with tooltip ──────────────────────────────────────────────────
const AvatarPill = ({ email, size = 28 }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="avatar-pill-wrap"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="avatar-pill"
        style={{
          width: size,
          height: size,
          backgroundColor: getAvatarColor(email),
          fontSize: size * 0.36,
        }}
      >
        {getInitials(email)}
      </div>
      {hovered && <div className="avatar-tooltip">{email}</div>}
    </div>
  );
};

// ── Status Chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ status, onClick, small }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  return (
    <button
      onClick={onClick}
      title={onClick ? "Click to cycle status" : ""}
      className={`status-chip ${onClick ? "clickable" : ""}`}
      style={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        border: `1.5px solid ${cfg.color}30`,
        padding: small ? "2px 8px" : "4px 11px",
        fontSize: small ? "0.68rem" : "0.75rem",
      }}
    >
      <FontAwesomeIcon
        icon={cfg.icon}
        style={{ fontSize: small ? "0.6rem" : "0.68rem" }}
      />
      {cfg.label}
    </button>
  );
};

// ── Subtask Row ───────────────────────────────────────────────────────────────
const SubtaskRow = ({
  subtask,
  taskId,
  onStatusChange,
  onDelete,
  isCreator,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState(subtask.summary || "");
  const [remarks, setRemarks] = useState(subtask.remarks || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const timerRef = useRef(null);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const tok = await getToken();
      await axios.patch(
        `${API_URL}/tasks/${taskId}/subtasks/${subtask._id}`,
        payload,
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if ("status" in payload) onStatusChange();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const debouncedSave = (field, val) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => patch({ [field]: val }), 2500);
  };

  const cycleStatus = (e) => {
    e.stopPropagation();
    const next =
      STATUS_CYCLE[
        (STATUS_CYCLE.indexOf(subtask.status) + 1) % STATUS_CYCLE.length
      ];
    patch({ status: next });
  };

  return (
    <div className={`subtask-row ${expanded ? "expanded" : ""}`}>
      <div
        className="subtask-row-header"
        onClick={() => setExpanded(!expanded)}
      >
        <StatusChip status={subtask.status} onClick={cycleStatus} small />
        <span className="subtask-title">{subtask.title}</span>
        {saving && (
          <FontAwesomeIcon icon={faSpinner} spin className="subtask-saving" />
        )}
        {saved && !saving && (
          <FontAwesomeIcon icon={faCheck} className="subtask-saved" />
        )}
        {isCreator && (
          <button
            className="subtask-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(subtask._id);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#DC3545")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = "var(--text-gray)")
            }
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          className="subtask-chevron"
        />
      </div>

      {expanded && (
        <div className="subtask-body">
          {[
            {
              key: "summary",
              label: "Summary",
              val: summary,
              set: setSummary,
              ph: "What is this subtask about?",
            },
            {
              key: "remarks",
              label: "Remarks",
              val: remarks,
              set: setRemarks,
              ph: "Running notes, blockers, updates...",
            },
          ].map(({ key, label, val, set, ph }) => (
            <div key={key} className="subtask-field">
              <label className="field-label">{label}</label>
              <textarea
                value={val}
                rows={3}
                placeholder={ph}
                onChange={(e) => {
                  set(e.target.value);
                  debouncedSave(key, e.target.value);
                }}
                className="subtask-textarea"
                onFocus={(e) =>
                  (e.target.style.borderColor = "var(--primary-color)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "var(--border-color)")
                }
              />
            </div>
          ))}
          {saved && (
            <p className="saved-indicator">
              <FontAwesomeIcon icon={faCheck} /> Saved
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Task Card ─────────────────────────────────────────────────────────────────
const TaskCard = ({
  task,
  currentUserEmail,
  onRefresh,
  allEmployees,
  taskRef,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [subTitle, setSubTitle] = useState("");
  const [subSummary, setSubSummary] = useState("");
  const [addSubLoading, setAddSubLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isCreator = !task.createdBy || task.createdBy === currentUserEmail;
  const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.not_started;

  const authH = async () => ({ Authorization: `Bearer ${await getToken()}` });

  const cycleStatus = async (e) => {
    e.stopPropagation();
    const scrollY = window.scrollY;
    const next =
      STATUS_CYCLE[
        (STATUS_CYCLE.indexOf(task.status) + 1) % STATUS_CYCLE.length
      ];
    setStatusLoading(true);
    try {
      await axios.patch(
        `${API_URL}/tasks/${task._id}/status`,
        { status: next },
        { headers: await authH() },
      );
      await onRefresh(true);
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    } catch (err) {
      console.error(err);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleAddSubtask = async () => {
    if (!subTitle.trim()) return;
    setAddSubLoading(true);
    try {
      await axios.post(
        `${API_URL}/tasks/${task._id}/subtasks`,
        { title: subTitle.trim(), summary: subSummary.trim() },
        { headers: await authH() },
      );
      setSubTitle("");
      setSubSummary("");
      setAddSubOpen(false);
      onRefresh(true);
    } catch (err) {
      console.error(err);
    } finally {
      setAddSubLoading(false);
    }
  };

  const handleDeleteSubtask = async (sid) => {
    try {
      await axios.delete(`${API_URL}/tasks/${task._id}/subtasks/${sid}`, {
        headers: await authH(),
      });
      onRefresh(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAssign = async (email) => {
    try {
      await axios.patch(
        `${API_URL}/tasks/${task._id}/assign`,
        { add: [email] },
        { headers: await authH() },
      );
      setAssignSearch("");
      onRefresh(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUnassign = async (email) => {
    try {
      await axios.patch(
        `${API_URL}/tasks/${task._id}/assign`,
        { remove: [email] },
        { headers: await authH() },
      );
      onRefresh(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/tasks/${task._id}`, {
        headers: await authH(),
      });
      onRefresh(true);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const assignSuggestions = allEmployees
    .filter(
      (e) =>
        e.toLowerCase().includes(assignSearch.toLowerCase()) &&
        !task.assignedTo.includes(e) &&
        e !== task.createdBy,
    )
    .slice(0, 6);

  const allAssigned = [task.createdBy, ...task.assignedTo].filter(Boolean);

  return (
    <div
      className="task-card"
      style={{ borderLeft: `4px solid ${cfg.color}` }}
      ref={taskRef}
    >
      <div className="task-card-header">
        <div className="task-card-left" onClick={() => setExpanded(!expanded)}>
          <AvatarPill email={task.createdBy} size={30} />
          <div className="task-card-title-wrap">
            <span className="task-card-title">{task.title}</span>
            <div className="task-card-meta">
              <span className="task-card-creator">
                by {task.createdBy || "Unknown"}
              </span>
              {task.subtasks.length > 0 && (
                <span className="task-subtask-progress">
                  {task.subtasks.filter((s) => s.status === "completed").length}
                  /{task.subtasks.length} done
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="task-card-right">
          <div className="assignees-row">
            {allAssigned.map((email) => (
              <div key={email} className="assignee-avatar-wrap">
                <AvatarPill email={email} size={26} />
                {isCreator && email !== task.createdBy && (
                  <button
                    className="assignee-remove"
                    onClick={() => handleUnassign(email)}
                    title={`Remove ${email}`}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            ))}
            {isCreator && (
              <div className="assign-btn-wrap">
                <button
                  className="assign-add-btn"
                  onClick={() => setAssignOpen(!assignOpen)}
                  title="Add person"
                >
                  <FontAwesomeIcon icon={faUserPlus} />
                </button>
                {assignOpen && (
                  <div className="assign-dropdown">
                    <div className="assign-search-wrap">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="assign-search-icon"
                      />
                      <input
                        autoFocus
                        value={assignSearch}
                        onChange={(e) => setAssignSearch(e.target.value)}
                        placeholder="Search email..."
                        className="assign-search-input"
                      />
                    </div>
                    {assignSuggestions.map((email) => (
                      <div
                        key={email}
                        className="assign-option"
                        onClick={() => {
                          handleAssign(email);
                          setAssignOpen(false);
                        }}
                      >
                        <AvatarPill email={email} size={22} />
                        <span>{email}</span>
                      </div>
                    ))}
                    {assignSearch && assignSuggestions.length === 0 && (
                      <div className="assign-empty">No match</div>
                    )}
                    <div
                      className="assign-backdrop"
                      onClick={() => setAssignOpen(false)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            className="task-action-btn"
            onClick={() => {
              setExpanded(true);
              setAddSubOpen(true);
            }}
            title="Add subtask"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>

          <StatusChip
            status={task.status}
            onClick={!statusLoading ? cycleStatus : null}
          />

          {isCreator && (
            <button
              className="task-action-btn danger"
              onClick={handleDelete}
              disabled={deleteLoading}
              title="Delete task"
            >
              {deleteLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faTrash} />
              )}
            </button>
          )}

          <button
            className="task-expand-btn"
            onClick={() => setExpanded(!expanded)}
          >
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="task-card-body">
          {addSubOpen && (
            <div className="add-subtask-form">
              <input
                autoFocus
                value={subTitle}
                onChange={(e) => setSubTitle(e.target.value)}
                placeholder="Subtask title *"
                className="add-subtask-input"
                onKeyDown={(e) =>
                  e.key === "Enter" && !e.shiftKey && handleAddSubtask()
                }
              />
              <textarea
                value={subSummary}
                onChange={(e) => setSubSummary(e.target.value)}
                placeholder="Summary (optional)"
                rows={2}
                className="add-subtask-textarea"
              />
              <div className="add-subtask-actions">
                <button
                  onClick={handleAddSubtask}
                  disabled={!subTitle.trim() || addSubLoading}
                  className="btn btn-primary btn-sm"
                >
                  {addSubLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Add"
                  )}
                </button>
                <button
                  onClick={() => {
                    setAddSubOpen(false);
                    setSubTitle("");
                    setSubSummary("");
                  }}
                  className="btn btn-outline btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {task.subtasks.length === 0 && !addSubOpen && (
            <div className="subtasks-empty">
              No subtasks yet.{" "}
              <span
                className="subtasks-empty-link"
                onClick={() => setAddSubOpen(true)}
              >
                Add one
              </span>
            </div>
          )}

          {task.subtasks.map((sub) => (
            <SubtaskRow
              key={sub._id}
              subtask={sub}
              taskId={task._id}
              onStatusChange={() => onRefresh(true)}
              onDelete={handleDeleteSubtask}
              isCreator={isCreator}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Person group ──────────────────────────────────────────────────────────────
const PersonGroup = ({ email, displayName, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="person-group">
      <div className="person-group-header" onClick={() => setOpen(!open)}>
        <AvatarPill email={email} size={26} />
        <span className="person-group-name">{displayName || email}</span>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className="person-group-chevron"
        />
      </div>
      {open && <div className="person-group-body">{children}</div>}
    </div>
  );
};

// ── Weekly bucket ─────────────────────────────────────────────────────────────
const WeekBucket = ({ weekStart, children, isCurrent }) => (
  <div className="week-bucket">
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
const WorkspacePage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [allEmployees, setAllEmployees] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [managerMap, setManagerMap] = useState({});
  const [navOpen, setNavOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [filterPerson, setFilterPerson] = useState("all");
  const taskRefs = useRef({});

  useEffect(() => {
    const init = async () => {
      const email = await localforage.getItem("email");
      if (email) setCurrentUserEmail(email.toLowerCase());
      try {
        const tok = await getToken();
        const res = await axios.get(`${API_URL}/employeedata/all-emails`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        setAllEmployees(res.data?.emails || []);
        setNameMap(res.data?.nameMap || {});
        setManagerMap(res.data?.managerMap || {});
      } catch (err) {
        console.warn("Employee list:", err.message);
      }
    };
    init();
  }, []);

  const fetchTasks = useCallback(async (preserveScroll = false) => {
    const scrollY = preserveScroll ? window.scrollY : 0;
    if (!preserveScroll) setLoading(true);
    setError("");
    try {
      const tok = await getToken();
      if (!tok) { setLoading(false); return; }
      const res = await axios.get(`${API_URL}/tasks/my-tasks`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setTasks(res.data.data || []);
      if (preserveScroll)
        requestAnimationFrame(() => window.scrollTo(0, scrollY));
    } catch (err) {
      setError("Failed to load tasks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) return;
    setCreateLoading(true);
    try {
      const tok = await getToken();
      if (!tok) return;
      const rmEmail = managerMap[currentUserEmail] || null;
      const assignedTo = rmEmail ? [rmEmail] : [];
      await axios.post(
        `${API_URL}/tasks/create`,
        { title: newTaskTitle.trim(), createdBy: currentUserEmail, assignedTo },
        { headers: { Authorization: `Bearer ${tok}` } },
      );
      setNewTaskTitle("");
      fetchTasks();
    } catch (err) {
      setError("Failed to create task.");
    } finally {
      setCreateLoading(false);
    }
  };

  const scrollToTask = (taskId) => {
    setNavOpen(false);
    const el = taskRefs.current[taskId];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ── Current week bounds (Wed → Tue) ──────────────────────────────────────
  const cwStart = useMemo(() => getCurrentWeekStart(), []);
  const cwEnd   = useMemo(() => getWeekEnd(cwStart), [cwStart]);

  // Only show tasks created within the current Wed→Tue window
  const myTasks = useMemo(
    () => tasks.filter((t) => {
      const d = new Date(t.createdAt || new Date());
      return d >= cwStart && d <= cwEnd &&
        (!t.createdBy || t.createdBy === currentUserEmail);
    }),
    [tasks, currentUserEmail, cwStart, cwEnd],
  );

  const otherTasks = useMemo(
    () => tasks.filter((t) => {
      const d = new Date(t.createdAt || new Date());
      return d >= cwStart && d <= cwEnd &&
        t.createdBy && t.createdBy !== currentUserEmail;
    }),
    [tasks, currentUserEmail, cwStart, cwEnd],
  );

  const otherCreators = useMemo(
    () => [...new Set(otherTasks.map((t) => t.createdBy).filter(Boolean))],
    [otherTasks],
  );
  const filteredOther =
    filterPerson === "all"
      ? otherTasks
      : otherTasks.filter((t) => t.createdBy === filterPerson);

  // Group by creator (no week grouping needed — all tasks are this week)
  const groupByCreator = (taskList) => {
    const map = {};
    taskList.forEach((t) => {
      const key = t.createdBy || "Unknown";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return Object.entries(map);
  };

  const myGrouped    = useMemo(() => groupByCreator(myTasks),    [myTasks]);
  const otherGrouped = useMemo(() => groupByCreator(filteredOther), [filteredOther]);

  const currentWeekStart = cwStart.getTime();

  const hasAnyTasks = myTasks.length > 0 || otherTasks.length > 0;

  return (
    <>
      <SideBar />
      <div
        className="dashboard-container-workflow"
        style={{ display: "flex", minHeight: "calc(100vh - 0px)" }}
      >
        <div className="workspace-root" style={{ flex: 1 }}>
          {/* ── Left panel ───────────────────────────────────────────── */}
          <div className={`task-nav ${navOpen ? "open" : "closed"}`}>
            <div className="task-nav-create">
              <div className="task-nav-create-label">
                {navOpen && "New Task"}
                <button
                  className="task-nav-toggle-inline"
                  onClick={() => setNavOpen(!navOpen)}
                  title={navOpen ? "Collapse" : "Expand"}
                >
                  <FontAwesomeIcon
                    icon={navOpen ? faChevronLeft : faChevronRight}
                  />
                </button>
              </div>
              <div className="task-nav-input-row">
                <input
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !e.shiftKey && handleCreateTask()
                  }
                  placeholder="Task name..."
                  className="task-nav-input"
                />
                <button
                  onClick={handleCreateTask}
                  disabled={!newTaskTitle.trim() || createLoading}
                  className="task-nav-create-btn"
                  title="Create task"
                >
                  {createLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faPlus} />
                  )}
                </button>
              </div>
              <div className="task-library">
                <div className="task-library-label">
                  <FontAwesomeIcon icon={faListUl} style={{ marginRight: 5 }} />
                  Task Library
                </div>
                <div className="task-library-list">
                  {TASK_SUGGESTIONS.map((s) => (
                    <div
                      key={s}
                      className={`task-library-item ${newTaskTitle === s ? "selected" : ""}`}
                      onClick={() => setNewTaskTitle(s)}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {myTasks.length > 0 && (
              <div className="task-nav-section">
                <div className="task-nav-section-label">
                  My Tasks ({myTasks.length})
                </div>
                {myTasks.map((t) => (
                  <div
                    key={t._id}
                    className="task-nav-item"
                    onClick={() => scrollToTask(t._id)}
                  >
                    <span
                      className="task-nav-dot"
                      style={{
                        backgroundColor:
                          STATUS_CONFIG[t.status]?.color || "#999",
                      }}
                    />
                    <span className="task-nav-item-title">{t.title}</span>
                  </div>
                ))}
              </div>
            )}

            {otherTasks.length > 0 && (
              <div className="task-nav-section">
                <div className="task-nav-section-label">
                  Assigned to Me ({otherTasks.length})
                </div>
                {otherTasks.map((t) => (
                  <div
                    key={t._id}
                    className="task-nav-item"
                    onClick={() => scrollToTask(t._id)}
                  >
                    <span
                      className="task-nav-dot"
                      style={{
                        backgroundColor:
                          STATUS_CONFIG[t.status]?.color || "#999",
                      }}
                    />
                    <span className="task-nav-item-title">{t.title}</span>
                  </div>
                ))}
              </div>
            )}

            {!hasAnyTasks && (
              <div className="task-nav-empty">
                No tasks yet — create one above
              </div>
            )}
          </div>

          <div
            className={`task-nav-backdrop ${navOpen ? "mobile-active" : ""}`}
            onClick={() => setNavOpen(false)}
          />

          {/* ── Main content ─────────────────────────────────────────── */}
          <div className="workspace-main">
            <div className="workspace-header">
              <h1 className="workspace-title">My Week</h1>
              <div className="workspace-week-badge">
                <span className="week-current-dot" />
                <span>{fmtWeekLabel(new Date(currentWeekStart))}</span>
              </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {loading && (
              <div className="tasks-loading">
                <div className="spinner" />
                <p>Loading tasks...</p>
              </div>
            )}

            {!loading && (
              <>
                {/* ── My Tasks ─────────────────────────────────────── */}
                {myTasks.length > 0 && (
                  <div className="tasks-section">
                    <div className="tasks-section-header">
                      <span className="tasks-section-title">My Tasks</span>
                      <span className="tasks-section-count">{myTasks.length}</span>
                    </div>
                    {myGrouped.map(([creator, creatorTasks]) => (
                      <PersonGroup
                        key={creator}
                        email={creator}
                        displayName={nameMap[creator]}
                      >
                        {creatorTasks.map((task) => (
                          <TaskCard
                            key={task._id}
                            task={task}
                            currentUserEmail={currentUserEmail}
                            onRefresh={fetchTasks}
                            allEmployees={allEmployees}
                            taskRef={(el) => { taskRefs.current[task._id] = el; }}
                          />
                        ))}
                      </PersonGroup>
                    ))}
                  </div>
                )}

                {/* ── Assigned to Me ───────────────────────────────── */}
                {otherTasks.length > 0 && (
                  <div className="tasks-section">
                    <div className="tasks-section-header">
                      <span className="tasks-section-title">Assigned to Me</span>
                      <span className="tasks-section-count">
                        {Object.keys(otherGrouped).length}
                      </span>
                      <div className="person-filter">
                        <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                        <select
                          value={filterPerson}
                          onChange={(e) => setFilterPerson(e.target.value)}
                          className="person-filter-select"
                        >
                          <option value="all">All People</option>
                          {otherCreators.map((c) => (
                            <option key={c} value={c}>
                              {nameMap[c] || c}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {otherGrouped.map(([creator, creatorTasks]) => (
                      <PersonGroup
                        key={creator}
                        email={creator}
                        displayName={nameMap[creator]}
                      >
                        {creatorTasks.map((task) => (
                          <TaskCard
                            key={task._id}
                            task={task}
                            currentUserEmail={currentUserEmail}
                            onRefresh={fetchTasks}
                            allEmployees={allEmployees}
                            taskRef={(el) => { taskRefs.current[task._id] = el; }}
                          />
                        ))}
                      </PersonGroup>
                    ))}
                  </div>
                )}

                {/* ── Empty state ───────────────────────────────────── */}
                {!hasAnyTasks && (
                  <div className="tasks-empty">
                    No tasks this week yet. Use the panel on the left to create one.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkspacePage;